import {
  ITEM_CARD_SEQUENCE_LIST_VIEW,
  NARRATIVE_VIEW,
  SPREADSHEET_VIEW,
  TIMELINE_VIEW
} from "../../constants/identifiers";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import interfaceUserActions from "../../constants/interfaceUserActions";
import { appStateActionTypes, appStateSelectors } from "../../modules/appState";
import { syncActionTypes } from "../../modules/sync";
import { syncSettingsActionTypes } from "../../modules/sync/settings";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { ExternalLinkIdentifiers } from "../tutorialLinks";
import {
  getEnterEditModeInstruction,
  getFlashPlaceholder,
  getIconPlaceholder,
  getLinkPlaceholder,
  getSplitViewInstruction,
  getSwitchViewInstruction,
  getTipIdGenerator,
  getViewNameLabel,
  Placeholders
} from "../utils";

const isDesktop = preprocessorQuery.isDesktop();
const isTablet = preprocessorQuery.isTablet();
const isMobile = preprocessorQuery.isMobile();

// -----------------------------------------------------
// IMPORTANT NOTES:
//
// GROUP_GATE / accessGate:
//    * expects a function with signature:
//        state => { return true; }
//    * return true to show tutorial
//    * return false to block tutorial and show message
//    * if function is null, will show tutorial
//
// resolve:
//    * expects a function with signature:
//       (action, newState, oldState) => { return true; }
//    * return true to mark the tip as resolved
//    * batch actions will be broken down by the time
//      this function is called, so you will receive the
//      individual actions
//
// -----------------------------------------------------

export const SharedTipIdentifiers = {
  // Shared introductory steps
  ORGANIZE_EVENTS: "ORGANIZE_EVENTS",
  DRAG_TO_TIMELINE: "DRAG_TO_TIMELINE",
  DRAG_TO_RESIZE: "DRAG_TO_RESIZE",
  SET_EVENT_DATE_TIMELINE: "SET_EVENT_DATE_TIMELINE",
  SET_EVENT_DATE_INSPECTOR: "SET_EVENT_DATE_INSPECTOR",

  // Shared syncing steps
  PREPARE_SYNC_PROJECT: "PREPARE_SYNC_PROJECT",
  CHANGE_SECTION_LABEL_SETTINGS: "CHANGE_SECTION_LABEL_SETTINGS",
  SELECT_SYNC_PROJECT: "SELECT_SYNC_PROJECT",
  FIRST_TIME_SYNC: "FIRST_TIME_SYNC",
  SYNC_OPEN_NARRATIVE_VIEW: "SYNC_OPEN_NARRATIVE_VIEW",
  CHANGE_NARRATIVE_SYNC_AGAIN: "CHANGE_NARRATIVE_SYNC_AGAIN",
  CHANGE_PROJECT_SYNC_AGAIN: "CHANGE_PROJECT_SYNC_AGAIN",
  MARK_DOCUMENTS_TO_IGNORE: "MARK_DOCUMENTS_TO_IGNORE"
};

let sharedTips = {};
if (isDesktop) {
  sharedTips[SharedTipIdentifiers.ORGANIZE_EVENTS] = {
    label: `Organize your ${Placeholders.EVENT_PLURAL_LABEL}`,
    steps: [
      {
        label: `${getViewNameLabel(SPREADSHEET_VIEW)} View allows you to order ${
          Placeholders.EVENT_PLURAL_LABEL
        }, even if they don't have dates`,
        isNumbered: false
      },
      {
        label: `Use the ${getFlashPlaceholder(
          interfaceIdentifiers.TABLE_VIEW_DRAG_HANDLE,
          "drag handles"
        )} ${getIconPlaceholder("dragHandle")} next to ${
          Placeholders.EVENT_PLURAL_LABEL
        } to drag them to a new position`,
        isNumbered: true
      },
      {
        label: `Drop between ${Placeholders.EVENT_PLURAL_LABEL} to re-order, or on top of ${Placeholders.EVENT_PLURAL_LABEL} to nest within`,
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    canSkip: true,
    requiresDismissal: true
  };

  sharedTips[SharedTipIdentifiers.DRAG_TO_TIMELINE] = {
    label: `Drag ${Placeholders.EVENT_PLURAL_LABEL} into Timeline View to give them dates`,
    steps: [
      getSplitViewInstruction(SPREADSHEET_VIEW, TIMELINE_VIEW),
      {
        label: `Select the ${Placeholders.EVENT_PLURAL_LABEL} you want in your timeline`,
        isNumbered: true
      },
      {
        label: `Use the ${getFlashPlaceholder(
          interfaceIdentifiers.TABLE_VIEW_DRAG_HANDLE,
          "drag handle"
        )} ${getIconPlaceholder("dragHandle")} next to a selected ${
          Placeholders.EVENT_LABEL
        } to drag it into the Timeline View.`,
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    requiresDismissal: true,
    canSkip: true
  };

  sharedTips[SharedTipIdentifiers.DRAG_TO_RESIZE] = {
    label: `Resize ${Placeholders.EVENT_PLURAL_LABEL} to give them durations`,
    steps: [
      {
        label: `In ${getViewNameLabel(TIMELINE_VIEW)} View, move your mouse over the right edge of ${
          Placeholders.AN_EVENT_LABEL
        } diamond until the resize cursor shows`,
        isNumbered: true
      },
      {
        label: "Click and drag the mouse to the right until you reach the duration you want",
        isNumbered: true
      },
      {
        label: `Release the mouse to update the ${Placeholders.EVENT_LABEL} duration`,
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    canSkip: true,
    resolve: action =>
      action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
      action.id === interfaceUserActions.RESIZE_IN_TIMELINE
  };

  sharedTips[SharedTipIdentifiers.SET_EVENT_DATE_TIMELINE] = {
    label: `Change ${Placeholders.EVENT_LABEL} dates in Timeline View`,
    steps: [
      {
        label: `In ${getViewNameLabel(TIMELINE_VIEW)} View, click and drag ${
          Placeholders.EVENT_PLURAL_LABEL
        } to change their date.`,
        isNumbered: true
      },
      {
        label: `The order of ${Placeholders.EVENT_PLURAL_LABEL} in the Spreadsheet will update as required`,
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    canSkip: true,
    resolve: action =>
      action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
      action.id === interfaceUserActions.DRAG_IN_TIMELINE
  };

  sharedTips[SharedTipIdentifiers.SET_EVENT_DATE_INSPECTOR] = {
    label: `Set ${Placeholders.EVENT_LABEL} dates in the Inspector`,
    steps: [
      {
        label: `You can also edit ${Placeholders.EVENT_LABEL} dates and duration in the Inspector`,
        isNumbered: true
      },

      {
        label: `Click on ${Placeholders.AN_EVENT_LABEL} to select it.`,
        isNumbered: true
      },
      {
        label: `In the Inspector, click on the ${getFlashPlaceholder(
          interfaceIdentifiers.INSPECTOR_DATES_TAB,
          "clock icon"
        )} ${getIconPlaceholder("inspectorTime")} to show the Dates tab`,
        isNumbered: true
      },
      {
        label: `Change the ${
          Placeholders.EVENT_LABEL
        }'s Start Date by typing a date or clicking on the calendar icon ${getIconPlaceholder("calendar")}`,
        isNumbered: true
      }
    ],
    requiresInspector: true,
    accessGate: null,
    gateMessage: null,
    canSkip: true,
    resolve: action =>
      action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
      action.id === interfaceUserActions.SET_START_DATE_INSPECTOR
  };

  sharedTips[SharedTipIdentifiers.PREPARE_SYNC_PROJECT] = {
    label: "Prepare your writing project ready to be synced",
    steps: [
      {
        label: `Determine which folder in your ${Placeholders.WRITING_APP} project contains your narrative`,
        isNumbered: true
      },
      {
        label: "Move documents that should be excluded from the narrative to outside that folder",
        isNumbered: true
      },
      {
        label: "Ensure documents have recognisable titles when viewed out of context",
        isNumbered: true
      },
      {
        label: `${getLinkPlaceholder(
          ExternalLinkIdentifiers.PREPARE_SYNC_PROJECT,
          "Organise your documents"
        )} into a consistent folder structure`,
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    requiresDismissal: true
  };

  sharedTips[SharedTipIdentifiers.CHANGE_SECTION_LABEL_SETTINGS] = {
    label: "Confirm narrative section labels",
    steps: [
      {
        label: `Take note of the structure in the Narrative folder in your ${Placeholders.WRITING_APP} project`,
        isNumbered: true
      },
      {
        label: `Click on the ${getFlashPlaceholder(
          interfaceIdentifiers.SETTINGS,
          "Settings icon"
        )} ${getIconPlaceholder("sidebarSettings")} in the toolbar to open Timeline Settings`,
        isNumbered: true
      },
      {
        label:
          "In the Narrative tab, remove section labels you don't need, and rename or rearrange the rest as required",
        isNumbered: true
      },
      {
        label: "Tick the section types that should be automatically applied to folders and other item types",
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    requiresDismissal: true
  };

  sharedTips[SharedTipIdentifiers.SELECT_SYNC_PROJECT] = {
    label: "Select writing project and configure sync settings",
    steps: [
      {
        label: `Click on the ${getFlashPlaceholder(interfaceIdentifiers.SYNC, "sync icon")} ${getIconPlaceholder(
          "sidebarSync"
        )} in the Sidebar, then click on ${getFlashPlaceholder(
          interfaceIdentifiers.SYNC_PANEL_SETTINGS,
          "Setup Syncing"
        )}`,
        isNumbered: true
      },
      {
        label: "Choose your writing app and if Scrivener, select your Scrivener project",
        isNumbered: true
      },
      {
        label: "In the Sync Folders tab, select the folder that contains your narrative",
        isNumbered: true
      },
      {
        label:
          "Use the defaults on the remaining screens, or configure extra sync options for data you already have setup in your project",
        isNumbered: true
      },
      {
        label: "Don't stress - you can change settings to add more fields later",
        isNumbered: false
      }
    ],
    accessGate: null,
    gateMessage: null,
    actionInstruction: `Select a sync project to continue`,
    resolve: action => action.type === syncSettingsActionTypes.REPLACE_SETTINGS
  };

  sharedTips[SharedTipIdentifiers.MARK_DOCUMENTS_TO_IGNORE] = {
    label: "Identify documents that should be omitted during syncing",
    steps: [
      {
        label: `In the ${getFlashPlaceholder(
          interfaceIdentifiers.SYNC,
          "sync panel"
        )}, identify documents such as title pages that you do not wish to import into your timeline narrative`,
        isNumbered: true
      },
      {
        label: "Click on one of those documents in the sync panel to select it",
        isNumbered: true
      },
      {
        label: `In the Inspector, click on the "Change" link next to "Create New" and choose "Ignore" from the dropdown menu`,
        isNumbered: true
      },
      {
        label: "Repeat for any other documents you wish to exclude",
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    requiresDismissal: true
  };

  sharedTips[SharedTipIdentifiers.FIRST_TIME_SYNC] = {
    label: `Sync your timeline with your ${Placeholders.WRITING_APP} project`,
    steps: [
      {
        label: `Click on the ${getFlashPlaceholder(
          interfaceIdentifiers.SYNC_PANEL_SYNC_NOW,
          "Sync Now"
        )} button at the top of the Sync Panel to import the items`,
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    resolve: action => action.type === syncActionTypes.EXECUTE_SYNC
  };

  sharedTips[SharedTipIdentifiers.SYNC_OPEN_NARRATIVE_VIEW] = {
    label: "View items in your Narrative",
    steps: [
      {
        label: `The narrative has its own ${getLinkPlaceholder(
          ExternalLinkIdentifiers.NARRATIVE_VS_CHRONOLOGY,
          "independent order"
        )} of ${Placeholders.EVENT_PLURAL_LABEL}`,
        isNumbered: true
      },
      getSwitchViewInstruction(NARRATIVE_VIEW),
      {
        label: `This will show all items in your narrative, including all of the items and folders imported from ${Placeholders.WRITING_APP}`,
        isNumbered: true
      },
      {
        label: `Items will not appear in your timeline yet, we will learn more about this in the next tutorial`,
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    requiresDismissal: true,
    dismissalGate: state => {
      const openViews = appStateSelectors.getOpenViews(state);
      return Boolean(openViews.byId.find(v => v.viewType === NARRATIVE_VIEW));
    }
  };

  sharedTips[SharedTipIdentifiers.CHANGE_NARRATIVE_SYNC_AGAIN] = {
    label: `Make a change in Narrative View and sync to ${Placeholders.WRITING_APP}`,
    steps: [
      {
        label: `In ${getViewNameLabel(NARRATIVE_VIEW)} View, make a change such as renaming, moving or adding an item`,
        isNumbered: true
      },
      {
        label: `A dot on the ${getFlashPlaceholder(interfaceIdentifiers.SYNC, "sync icon")} ${getIconPlaceholder(
          "sidebarSyncActive"
        )} in the Sidebar will indicate there are changes to be synced`,
        isNumbered: true
      },
      {
        label: `Open the ${getFlashPlaceholder(
          interfaceIdentifiers.SYNC,
          "Sync Panel"
        )} and click ${getFlashPlaceholder(interfaceIdentifiers.SYNC_PANEL_SYNC_NOW, "Sync Now")}`,
        isNumbered: true
      },
      {
        label: `In ${Placeholders.WRITING_APP}, you will see the change that you made has been applied`,
        isNumbered: true
      },
      {
        label: `Tip: Syncing works best if you sync frequently, as soon as changes are detected`,
        isNumbered: false
      }
    ],
    accessGate: null,
    gateMessage: null,
    requiresDismissal: true
  };

  sharedTips[SharedTipIdentifiers.CHANGE_PROJECT_SYNC_AGAIN] = {
    label: `Make a change in ${Placeholders.WRITING_APP} and sync`,
    steps: [
      {
        label: `In ${Placeholders.WRITING_APP}, change the title of a synced document, or move or create a document in your narrative folder`,
        isNumbered: true
      },
      {
        label: `In Aeon Timeline, a dot on the ${getFlashPlaceholder(
          interfaceIdentifiers.SYNC,
          "sync icon"
        )} ${getIconPlaceholder("sidebarSyncActive")} in the Sidebar will indicate there are changes to be synced`,
        isNumbered: true
      },
      {
        label: `Open the ${getFlashPlaceholder(
          interfaceIdentifiers.SYNC,
          "Sync Panel"
        )} and click ${getFlashPlaceholder(interfaceIdentifiers.SYNC_PANEL_SYNC_NOW, "Sync Now")}`,
        isNumbered: true
      },
      {
        label: `In ${getViewNameLabel(NARRATIVE_VIEW)} View, you will see the change that you made has been applied`,
        isNumbered: true
      },
      {
        label: `Tip: Syncing works best if you sync frequently, as soon as changes are detected`,
        isNumbered: false
      }
    ],
    accessGate: null,
    gateMessage: null,
    requiresDismissal: true
  };
} else if (isTablet) {
  sharedTips[SharedTipIdentifiers.ORGANIZE_EVENTS] = {
    label: `Organize your ${Placeholders.EVENT_PLURAL_LABEL}`,
    steps: [
      {
        label: `${getViewNameLabel(SPREADSHEET_VIEW)} View allows you to order ${
          Placeholders.EVENT_PLURAL_LABEL
        }, even if they don't have dates`,
        isNumbered: false
      },
      getEnterEditModeInstruction(SPREADSHEET_VIEW),
      {
        label: `Use the ${getFlashPlaceholder(
          interfaceIdentifiers.TABLE_VIEW_DRAG_HANDLE,
          "drag handles"
        )} ${getIconPlaceholder("dragHandle")} next to ${
          Placeholders.EVENT_PLURAL_LABEL
        } to drag them to a new position`,
        isNumbered: true
      },
      {
        label: `Drop between ${Placeholders.EVENT_PLURAL_LABEL} to re-order, or on top of ${Placeholders.EVENT_PLURAL_LABEL} to nest within`,
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    canSkip: true,
    requiresDismissal: true
  };

  sharedTips[SharedTipIdentifiers.DRAG_TO_TIMELINE] = {
    label: `Drag ${Placeholders.EVENT_PLURAL_LABEL} into Timeline View to give them dates`,
    steps: [
      getSplitViewInstruction(SPREADSHEET_VIEW, TIMELINE_VIEW),
      {
        label: `Select the ${Placeholders.EVENT_PLURAL_LABEL} you want in your timeline`,
        isNumbered: true
      },
      getEnterEditModeInstruction(SPREADSHEET_VIEW),
      {
        label: `Use the ${getFlashPlaceholder(
          interfaceIdentifiers.TABLE_VIEW_DRAG_HANDLE,
          "drag handle"
        )} ${getIconPlaceholder("dragHandle")} next to a selected ${
          Placeholders.EVENT_LABEL
        } to drag it into the Timeline View.`,
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    requiresDismissal: true,
    canSkip: true
  };
  sharedTips[SharedTipIdentifiers.SET_EVENT_DATE_TIMELINE] = {
    label: `Change ${Placeholders.EVENT_LABEL} dates in Timeline View`,
    steps: [
      getEnterEditModeInstruction(TIMELINE_VIEW),
      {
        label: `Press and drag ${Placeholders.EVENT_PLURAL_LABEL} to change their date.`,
        isNumbered: true
      },
      {
        label: `The order of ${Placeholders.EVENT_PLURAL_LABEL} in the Spreadsheet will update as required`,
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    canSkip: true,
    resolve: action =>
      action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
      action.id === interfaceUserActions.DRAG_IN_TIMELINE
  };

  sharedTips[SharedTipIdentifiers.SET_EVENT_DATE_INSPECTOR] = {
    label: `Set ${Placeholders.EVENT_LABEL} dates in the Inspector`,
    steps: [
      {
        label: `You can also edit ${Placeholders.EVENT_LABEL} dates and duration in the Inspector`,
        isNumbered: true
      },
      {
        label: `Tap on ${Placeholders.AN_EVENT_LABEL} to edit the ${Placeholders.EVENT_LABEL} details`,
        isNumbered: true
      },
      {
        label: `In the Inspector, tap on "Date and Narrative"`,
        isNumbered: true
      },
      {
        label: `Tap on "Start" to begin editing the start date, and either type a new date or click on a new date in the calendar picker`,
        isNumbered: true
      }
    ],
    requiresInspector: true,
    requiresExitEditMode: true,
    accessGate: null,
    gateMessage: null,
    canSkip: true,
    resolve: action =>
      action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
      action.id === interfaceUserActions.SET_START_DATE_INSPECTOR
  };
} else if (isMobile) {
  sharedTips[SharedTipIdentifiers.ORGANIZE_EVENTS] = {
    label: `Organize your ${Placeholders.EVENT_PLURAL_LABEL}`,
    steps: [
      {
        label: `${getViewNameLabel(ITEM_CARD_SEQUENCE_LIST_VIEW)} View allows you to order ${
          Placeholders.EVENT_PLURAL_LABEL
        }, even if they don't have dates`,
        isNumbered: false
      },
      getEnterEditModeInstruction(ITEM_CARD_SEQUENCE_LIST_VIEW),
      {
        label: `Use the drag handles ${getIconPlaceholder("dragHandle")} next to ${
          Placeholders.EVENT_PLURAL_LABEL
        } to drag them to a new position`,
        isNumbered: true
      },
      {
        label: `Drop between ${Placeholders.EVENT_PLURAL_LABEL} to re-order, or on top of ${Placeholders.EVENT_PLURAL_LABEL} to nest within`,
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    canSkip: true,
    requiresDismissal: true
  };

  sharedTips[SharedTipIdentifiers.SET_EVENT_DATE_TIMELINE] = {
    label: `Change ${Placeholders.EVENT_LABEL} dates in Timeline View`,
    steps: [
      getEnterEditModeInstruction(TIMELINE_VIEW),
      {
        label: `Press and drag ${Placeholders.EVENT_PLURAL_LABEL} to change their date.`,
        isNumbered: true
      },
      {
        label: `The order of ${Placeholders.EVENT_PLURAL_LABEL} in the Spreadsheet will update as required`,
        isNumbered: true
      }
    ],
    accessGate: null,
    gateMessage: null,
    canSkip: true,
    resolve: action =>
      action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
      action.id === interfaceUserActions.DRAG_IN_TIMELINE
  };

  sharedTips[SharedTipIdentifiers.SET_EVENT_DATE_INSPECTOR] = {
    label: `Set ${Placeholders.EVENT_LABEL} dates in the Inspector`,
    steps: [
      {
        label: `You can set ${Placeholders.EVENT_LABEL} dates and duration in the Inspector`,
        isNumbered: true
      },
      {
        label: `Tap on ${Placeholders.AN_EVENT_LABEL} to edit the ${Placeholders.EVENT_LABEL} details`,
        isNumbered: true
      },
      {
        label: `In the Inspector, tap on "Date and Narrative"`,
        isNumbered: true
      },
      {
        label: `Tap on "Start" to begin editing the start date, and either type a new date or click on a new date in the calendar picker`,
        isNumbered: true
      }
    ],
    requiresInspector: true,
    requiresExitEditMode: true,
    accessGate: null,
    gateMessage: null,
    canSkip: true,
    resolve: action =>
      action.type === appStateActionTypes.USER_INTERFACE_ACTION_PERFORMED &&
      action.id === interfaceUserActions.SET_START_DATE_INSPECTOR
  };
}

const getSharedTip = (tipId, groupId) =>
  sharedTips[tipId]
    ? Object.assign({}, sharedTips[tipId], {
      id: getTipIdGenerator(groupId)(tipId)
    })
    : null;

export default getSharedTip;
